import { ContentCourse, Question } from "../../modules/lcms/content-courses/schemas/schemas";
import { convertJsonToStandardQuestion, convertJsonToStandardCourse } from "../../utils/courseConverters";
import { rawQuestionBankData, rawContentCourseData, contentCourse } from "../mock-data";

// Import airport ground services courses
import airportSafetyCourse from "../mock/viag/courses/airport-safety-course.json";
import baggageHandlingCourse from "../mock/viag/courses/baggage-handling-course.json";
import aircraftMarshallingCourse from "../mock/viag/courses/aircraft-marshalling-course.json";
import customerServiceCourse from "../mock/viag/courses/customer-service-course.json";
import emergencyResponseCourse from "../mock/viag/courses/emergency-response-course.json";

export interface ImportCourseRequest {
  file: File;
  options?: {
    overwrite?: boolean;
    validateOnly?: boolean;
  };
}

// Generate standardized mock data
const generateStandardQuestionBank = (): Question[] => {
  return rawQuestionBankData.map(convertJsonToStandardQuestion);
};

const generateStandardContentCourse = (): ContentCourse => {
  return convertJsonToStandardCourse(rawContentCourseData);
};

// Create mock content courses that match the CourseDetailPage course IDs (AGS001-AGS012)
const createAviationGroundServicesCourses = (): ContentCourse[] => {
  const courseTemplates = [
    {
      id: "AGS001",
      name: "Cân bằng trọng tải tàu A320/A321 hãng Vietnam Airlines (VN)",
      title: "Đào tạo nghiệp vụ cân bằng trọng tải A320/A321",
      description: "Đào tạo nghiệp vụ cân bằng trọng tải và phân phối tải trọng cho các dòng tàu bay A320/A321 của Vietnam Airlines. Khóa học bao gồm lý thuyết về cân bằng trọng tải, sử dụng phần mềm LCS và thực hành với các tình huống thực tế.",
      category: "Cân bằng trọng tải",
      modules: [
        {
          module_id: "ags001-mod-001",
          name: "Module 1: Nguyên lý cân bằng trọng tải cơ bản",
          type: "combined",
          duration: 8,
          start_day_offset: 0,
          start_day_offset_relative_to: "course" as const,
          start_day_offset_reference_id: undefined,
          sections: [
            {
              section_id: "ags001-sec-001",
              name: "Phần 1: Giới thiệu về cân bằng trọng tải",
              order: 1,
              duration: 4,
              type: "file",
              start_day_offset: 0,
              start_day_offset_relative_to: "course" as const,
              file_id: "ags001-sec-001-file-001",
              file: {
                file_id: "ags001-sec-001-file-001",
                file_name: "Giới thiệu về cân bằng trọng tải.pdf",
                file_type: "pdf",
                file_size: 1000000,
                file_url: "https://www.google.com"
              }
            },
            {
              section_id: "ags001-sec-002",
              name: "Phần 2: Quy định và tiêu chuẩn",
              order: 2,
              duration: 4,
              type: "file",
              start_day_offset: 1,
              start_day_offset_relative_to: "section" as const,
              start_day_offset_reference_id: "ags001-sec-001",
            }
          ]
        },
        {
          module_id: "ags001-mod-002",
          name: "Module 2: Phần mềm Load Control System",
          type: "combined",
          duration: 12,
          start_day_offset: 3,
          start_day_offset_relative_to: "course" as const,
          sections: [
            {
              section_id: "ags001-sec-003",
              name: "Phần 1: Giao diện LCS Vietnam Airlines",
              order: 1,
              duration: 6,
              type: "file",
              start_day_offset: 0,
              start_day_offset_relative_to: "module" as const,
              start_day_offset_reference_id: "ags001-mod-002",
            },
            {
              section_id: "ags001-sec-004",
              name: "Phần 2: Xử lý tình huống đặc biệt",
              order: 2,
              duration: 6,
              type: "file",
              start_day_offset: 2,
              start_day_offset_relative_to: "section" as const,
              start_day_offset_reference_id: "ags001-sec-003",
            }
          ]
        },
        {
          module_id: "ags001-mod-003",
          name: "Module 3: Thực hành tại sân bay",
          type: "combined",
          duration: 16,
          start_day_offset: 7,
          start_day_offset_relative_to: "course" as const,
          sections: [
            {
              section_id: "ags001-sec-005",
              name: "Phần 1: Tham quan khu vực Load Control",
              order: 1,
              duration: 8,
              type: "file",
              start_day_offset: 0,
              start_day_offset_relative_to: "module" as const,
              start_day_offset_reference_id: "ags001-mod-003",
            },
            {
              section_id: "ags001-sec-006",
              name: "Phần 2: Đánh giá và chứng nhận",
              order: 2,
              duration: 8,
              type: "file",
              start_day_offset: 3,
              start_day_offset_relative_to: "section" as const,
              start_day_offset_reference_id: "ags001-sec-005",
            }
          ]
        }
      ]
    },
    {
      id: "AGS002",
      name: "An ninh hàng không dành cho nhân viên phục vụ kỹ thuật thương mại mặt đất",
      title: "Đào tạo an ninh hàng không cho nhân viên mặt đất",
      description: "Đào tạo các quy định an ninh hàng không cho nhân viên phục vụ kỹ thuật thương mại mặt đất tại cảng hàng không, sân bay. Bao gồm các quy định quốc tế, quy trình kiểm tra an ninh và xử lý tình huống.",
      category: "An ninh & An toàn",
      modules: [
        {
          module_id: "ags002-mod-001",
          name: "Module 1: Quy định an ninh cơ bản",
          type: "e-learning",
          duration: 8,
          start_day_offset: 0,
          start_day_offset_relative_to: "course" as const,
          sections: [
            {
              section_id: "ags002-sec-001",
              name: "Phần 1: Tổng quan an ninh hàng không",
              order: 1,
              delivery_method: "e-learning",
              duration: 4,
              start_day_offset: 0,
              start_day_offset_relative_to: "course" as const,
              activities: [
                { activity_id: "ags002-act-101", type: "lesson", title: "Bài học: Lịch sử và tầm quan trọng an ninh HK", duration: 1 },
                { activity_id: "ags002-act-102", type: "lesson", title: "Video: Các mối đe dọa an ninh thường gặp", duration: 1 },
                { activity_id: "ags002-act-103", type: "lesson", title: "Bài học: Quy định CAAV về an ninh", duration: 1.5 },
                { activity_id: "ags002-act-104", type: "exercise", title: "Quiz: Kiểm tra hiểu biết cơ bản", duration: 0.5 }
              ]
            },
            {
              section_id: "ags002-sec-002",
              name: "Phần 2: Quy trình kiểm tra an ninh",
              order: 2,
              delivery_method: "live-online",
              duration: 4,
              start_day_offset: 1,
              start_day_offset_relative_to: "section" as const,
              start_day_offset_reference_id: "ags002-sec-001",
              activities: [
                { activity_id: "ags002-act-201", type: "lesson", title: "Bài học: Thiết bị kiểm tra an ninh", duration: 1.5 },
                { activity_id: "ags002-act-202", type: "session", title: "Demo: Sử dụng máy X-ray", duration: 2 },
                { activity_id: "ags002-act-203", type: "exercise", title: "Thực hành: Nhận diện vật cấm", duration: 0.5 }
              ]
            }
          ]
        },
        {
          module_id: "ags002-mod-002",
          name: "Module 2: Xử lý tình huống khẩn cấp",
          type: "combined",
          duration: 10,
          start_day_offset: 3,
          start_day_offset_relative_to: "course" as const,
          sections: [
            {
              section_id: "ags002-sec-003",
              name: "Phần 1: Mô phỏng tình huống",
              order: 1,
              delivery_method: "in-person",
              duration: 6,
              start_day_offset: 0,
              start_day_offset_relative_to: "module" as const,
              start_day_offset_reference_id: "ags002-mod-002",
              activities: [
                { activity_id: "ags002-act-301", type: "session", title: "Simulation: Phát hiện vật cấm", duration: 2 },
                { activity_id: "ags002-act-302", type: "session", title: "Roleplay: Xử lý hành khách khó khăn", duration: 2 },
                { activity_id: "ags002-act-303", type: "session", title: "Workshop: Giao tiếp khẩn cấp", duration: 2 }
              ]
            },
            {
              section_id: "ags002-sec-004",
              name: "Phần 2: Đánh giá và chứng nhận",
              order: 2,
              delivery_method: "offline",
              duration: 4,
              start_day_offset: 2,
              start_day_offset_relative_to: "section" as const,
              start_day_offset_reference_id: "ags002-sec-003",
              activities: [
                { activity_id: "ags002-act-401", type: "assessment", title: "Thi lý thuyết: Quy định an ninh", duration: 2 },
                { activity_id: "ags002-act-402", type: "assessment", title: "Thi thực hành: Xử lý tình huống", duration: 2 }
              ]
            }
          ]
        }
      ]
    },
    {
      id: "AGS003",
      name: "Hệ thống kiểm soát tải của hãng hàng không",
      title: "Đào tạo hệ thống LCS quốc tế",
      description: "Đào tạo sử dụng các hệ thống kiểm soát tải của các hãng hàng không quốc tế như Singapore Airlines, EVA Air. Khóa học tập trung vào việc làm quen với các giao diện khác nhau và quy trình cụ thể của từng hãng.",
      category: "Hệ thống IT",
      modules: [
        {
          module_id: "ags003-mod-001",
          name: "Module 1: Singapore Airlines LCS",
          type: "e-learning",
          duration: 12,
          start_day_offset: 0,
          start_day_offset_relative_to: "course" as const,
          sections: [
            {
              section_id: "ags003-sec-001",
              name: "Phần 1: Giao diện SQ LCS",
              order: 1,
              delivery_method: "e-learning",
              duration: 6,
              start_day_offset: 0,
              start_day_offset_relative_to: "course" as const,
              activities: [
                { activity_id: "ags003-act-101", type: "lesson", title: "Bài học: Tổng quan hệ thống SQ", duration: 2 },
                { activity_id: "ags003-act-102", type: "lesson", title: "Demo: Giao diện chính SQ LCS", duration: 2 },
                { activity_id: "ags003-act-103", type: "exercise", title: "Thực hành: Đăng nhập và điều hướng", duration: 1.5 },
                { activity_id: "ags003-act-104", type: "exercise", title: "Bài tập: Nhập flight schedule", duration: 0.5 }
              ]
            },
            {
              section_id: "ags003-sec-002",
              name: "Phần 2: Quy trình SQ",
              order: 2,
              delivery_method: "live-online",
              duration: 6,
              start_day_offset: 2,
              start_day_offset_relative_to: "section" as const,
              start_day_offset_reference_id: "ags003-sec-001",
              activities: [
                { activity_id: "ags003-act-201", type: "session", title: "Workshop: Load planning SQ B777", duration: 3 },
                { activity_id: "ags003-act-202", type: "session", title: "Thực hành: ULD management", duration: 2 },
                { activity_id: "ags003-act-203", type: "exercise", title: "Case study: SQ long haul flight", duration: 1 }
              ]
            }
          ]
        },
        {
          module_id: "ags003-mod-002",
          name: "Module 2: EVA Air LCS",
          type: "live-online",
          duration: 8,
          start_day_offset: 5,
          start_day_offset_relative_to: "course" as const,
          sections: [
            {
              section_id: "ags003-sec-003",
              name: "Phần 1: Hệ thống EVA Air",
              order: 1,
              delivery_method: "live-online",
              duration: 8,
              start_day_offset: 0,
              start_day_offset_relative_to: "module" as const,
              start_day_offset_reference_id: "ags003-mod-002",
              activities: [
                { activity_id: "ags003-act-301", type: "lesson", title: "Bài học: Đặc điểm hệ thống EVA", duration: 1.5 },
                { activity_id: "ags003-act-302", type: "session", title: "Demo live: EVA LCS interface", duration: 3 },
                { activity_id: "ags003-act-303", type: "session", title: "Thực hành: Cargo loading EVA", duration: 3 },
                { activity_id: "ags003-act-304", type: "exercise", title: "Đánh giá: EVA procedures", duration: 0.5 }
              ]
            }
          ]
        }
      ]
    }
  ];

  return courseTemplates.map(template => ({
    id: template.id,
    name: template.name,
    title: template.title,
    description: template.description,
    status: "approved",
    createdBy: "Training Admin",
    author: "Aviation Training Center",
    createdAt: "2025-01-15T00:00:00Z",
    updatedAt: "2025-02-10T00:00:00Z",
    duration: template.modules ? (template.modules as any[]).reduce((total: number, module: any) => total + (module.duration || 0), 0) : 0,
    estimated_hours: template.modules ? (template.modules as any[]).reduce((total: number, module: any) => total + (module.duration || 0), 0) : 0,
    weeks: Math.ceil((template.modules ? (template.modules as any[]).reduce((total: number, module: any) => total + (module.duration || 0), 0) : 0) / 40),
    level: "intermediate",
    course_type: "professional_training",
    category: template.category,
    modules: template.modules?.map((module: any) => ({
      id: module.id,
      name: module.name,
      type: module.type,
      duration: module.duration,
      start_day_offset: module.start_day_offset,
      start_day_offset_relative_to: module.start_day_offset_relative_to,
      sections: module.sections?.map((section: any) => ({
        section_id: section.section_id,
        name: section.name,
        order: section.order,
        delivery_method: section.delivery_method,
        duration: section.duration,
        start_day_offset: section.start_day_offset,
        start_day_offset_relative_to: section.start_day_offset_relative_to,
        activities: section.activities?.map((activity: any) => ({
          activity_id: activity.activity_id,
          type: activity.type,
          title: activity.title,
          duration: activity.duration,
          default_platform: activity.type === 'session' ? 
            ['Microsoft Teams', 'Zoom', 'Google Meet', 'Classroom'][Math.floor(Math.random() * 4)] : undefined,
          default_location: activity.type === 'session' ? 
            ['Phòng đào tạo A101', 'Sân bay Nội Bài - Khu vực Training', 'Phòng mô phỏng', 'Khu vực Load Control'][Math.floor(Math.random() * 4)] : undefined,
          questions: activity.type === 'exercise' || activity.type === 'assessment' ? [
            {
              question_id: `${activity.activity_id}-q1`,
              question_text: `Câu hỏi liên quan đến ${activity.title}`,
              question_type: "multiple_choice" as const,
              options: ["Đáp án A", "Đáp án B", "Đáp án C", "Đáp án D"],
              correct_option: "Đáp án A"
            }
          ] : []
        })) || []
      })) || []
    })) || []
  })) as ContentCourse[];
};

// Convert all airport ground services courses
const generateAirportGroundServicesCourses = (): ContentCourse[] => {
  return [
    convertJsonToStandardCourse(airportSafetyCourse),
    convertJsonToStandardCourse(baggageHandlingCourse),
    convertJsonToStandardCourse(aircraftMarshallingCourse),
    convertJsonToStandardCourse(customerServiceCourse),
    convertJsonToStandardCourse(emergencyResponseCourse)
  ];
};

// Generate complete courses list including original mock data and airport courses
const generateStandardContentCourses = (): ContentCourse[] => {
  const aviationGroundHandlingCourses = createAviationGroundServicesCourses();
  const originalAirportCourses = generateAirportGroundServicesCourses();
  
  return [
    ...aviationGroundHandlingCourses, // AGS001-AGS012 courses matching CourseDetailPage
    ...originalAirportCourses // Original airport courses
  ];
};

// Mock service implementation with simulated async behavior
export const courseContentMockService = {
  getCourse: async (courseId: string): Promise<ContentCourse> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const courses = generateStandardContentCourses();
        let course: ContentCourse | undefined = courses.find(c => c.id === courseId);
        
        if (!course) {
          course = contentCourse;
        }
        
        resolve(course);
      }, 500);
    });
  },

  getCourses: async (): Promise<ContentCourse[]> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(generateStandardContentCourses());
      }, 500);
    });
  },

  createCourse: async (courseData: Partial<ContentCourse>): Promise<ContentCourse> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const newCourse: ContentCourse = {
          ...contentCourse,
          ...courseData,
          id: `course-${Date.now()}`,
          name: courseData.name || courseData.title || "New Course",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        resolve(newCourse);
      }, 500);
    });
  },

  updateCourse: async (courseId: string, courseData: Partial<ContentCourse>): Promise<ContentCourse> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const courses = generateStandardContentCourses();
        const courseIndex = courses.findIndex(c => c.id === courseId);
        if (courseIndex !== -1) {
          const updatedCourse = { ...courses[courseIndex], ...courseData };
          resolve(updatedCourse);
        } else {
          resolve({ ...contentCourse, ...courseData });
        }
      }, 500);
    });
  },

  deleteCourse: async (courseId: string): Promise<void> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 500);
    });
  },

  importCourse: async (request: ImportCourseRequest): Promise<ContentCourse> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const importedCourse: ContentCourse = {
          ...contentCourse,
          id: `imported-${Date.now()}`,
          name: "Imported Course",
          title: "Imported Course",
          description: "Course imported from file",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        resolve(importedCourse);
      }, 1000);
    });
  },

  getQuestionBank: async (): Promise<Question[]> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(generateStandardQuestionBank());
      }, 500);
    });
  },
}; 